import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import { Box, FormControl, IconButton, Typography } from "@mui/material";
import React, { type CSSProperties, type MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { INK, STATUS } from "../../../../theme/tokens";
import type { StaffingIssue } from "../../../../utils/autoStaffing";
import { formatPersonFullName } from "../../../../utils/formatters";
import {
	NotificationType,
	type NurseData,
	NurseFullRole,
	NurseFullRoleShort,
	NurseRole,
	type OpenShiftRequest,
	type ReplaceShiftRequest,
	ShiftType,
} from "../../../../utils/models";
import { generateShiftData } from "../../../../utils/nursePanelUtils";
import { getEmploymentTypeDisplayName, getUniqueLocations } from "../../../../utils/utilFunctions";
import { useStaffingTargetCheck } from "../../../../utils/warningDialogs/useStaffingTargetCheck";
import { useNurseWarnings, WarningDialog } from "../../../../utils/warningDialogs/WarningDialog";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { InitiateRoboCallButton } from "../../../InitiateRoboCallButton/InitiateRoboCallButton";
import { useRequestUtils } from "../../../RequestData/Provider/useRequestUtils";
import { MessageWorkingFilterEnum } from "../../../StaffCommunication/util/Broadcast/MessageFilter";
import { getShiftPickupMessage, NewBroadcastMessageModal } from "../../../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import { SystemDropdown } from "../../../UIToolkit/Inputs/SystemDropDown";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";
import { useRequestApproval } from "../../hooks/useRequestApproval";
import { MinimizedNurseDataTable } from "./MinimizedNurseDataTable";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	issue: StaffingIssue;
	onSubmit: (e: React.MouseEvent<HTMLElement>, suggestionIndex: number, nurseId?: number, location?: string) => void;
	onCancel: MouseEventHandler<HTMLButtonElement>;
	isLoading?: boolean;
	onDenyNurse?: (nurseId: number) => void;
	onRestoreNurse?: (nurseId: number) => void;
	queuedDenyNurseIds?: number[];
};

const maxTableHeight: CSSProperties["maxHeight"] = "200px";

export const MinimizedRequestApproval = ({ request, issue, onSubmit, onCancel, isLoading, onDenyNurse, onRestoreNurse, queuedDenyNurseIds }: Props) => {
	const { chosenDepartment, getNurseById, getWeekIndexByDateTime, hospitalData, getDayIndexByDateTime, isSnf, locationsMap, isMedicalDepartment } =
		useHospitalData();
	const lastNameFirst = useLastNameFirst();
	const { data: requestApprovalList, isFetching } = useRequestApproval({
		departmentId: chosenDepartment.department_id,
		requestId: request.id,
	});
	const queuedDenyIdSet = useMemo(() => new Set(queuedDenyNurseIds ?? []), [queuedDenyNurseIds]);
	const hasQueuedDenies = queuedDenyIdSet.size > 0;
	const [selectedNurseId, setSelectedNurseId] = useState<number | undefined>();
	const [selectedRoom, setSelectedRoom] = useState<string>(request.location || "");
	const rooms = useMemo(() => getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location), [chosenDepartment]);

	// Submit args parked while the staffing-target warning is shown; a ref so onComplete (which runs
	// synchronously within checkWarnings) reads the latest value rather than a stale closure.
	const pendingSubmitRef = useRef<{ event: React.MouseEvent<HTMLElement>; suggestionIndex: number; nurseId?: number; location?: string } | null>(null);
	const getTargetCheck = useStaffingTargetCheck();
	const performSubmit = useCallback(() => {
		const pending = pendingSubmitRef.current;
		pendingSubmitRef.current = null;
		if (!pending) return;
		onSubmit(pending.event, pending.suggestionIndex, pending.nurseId, pending.location);
	}, [onSubmit]);
	const [warningState, checkWarnings, handleAgencyConfirm, handleOvertimeConfirm, handleDailyHoursConfirm, handleCancelWarnings, , handleTargetConfirm] =
		useNurseWarnings(performSubmit, chosenDepartment.longShiftDuration);

	const isReplaceRequest = request.request_type === NotificationType.ReplaceShift;

	const replaceNurseLocation = useMemo(() => {
		if (!isReplaceRequest) return undefined;
		const weekIndex = getWeekIndexByDateTime(request.message.shift_start);
		const dayIndex = getDayIndexByDateTime(request.message.shift_start);
		const shiftIndex = request.message.shift_period;
		if (weekIndex === -1) return undefined;
		return hospitalData.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.days[dayIndex]?.nurses.find(
			(n) => n.nurseId === (request as ReplaceShiftRequest).replacement_nurse_id,
		)?.location;
	}, [isReplaceRequest, request, hospitalData, getWeekIndexByDateTime, getDayIndexByDateTime]);

	useEffect(() => {
		const matched = rooms.find((r) => r.toLowerCase() === (replaceNurseLocation ?? "").toLowerCase()) ?? "";
		setSelectedRoom(request.location || matched);
	}, [rooms, replaceNurseLocation]);
	const { isPartialShift } = useRequestUtils();

	const nursesDataFromBackend = useMemo(() => {
		const dayIndex = getDayIndexByDateTime(request.message.shift_start);
		return (requestApprovalList || []).map<NurseData>((nurseData) => {
			const currentRequestData = request.message.request_data.find((data) => data.nurse_id === nurseData.id);
			const nurse = getNurseById(nurseData.id);
			if (!nurse) {
				return {
					...nurseData,
					accepted_start_time: currentRequestData?.accepted_start_time ?? nurseData.accepted_start_time,
					accepted_end_time: currentRequestData?.accepted_end_time ?? nurseData.accepted_end_time,
				};
			}
			return {
				...nurseData,
				shifts: generateShiftData({
					nurse: nurse,
					weekIndex: getWeekIndexByDateTime(request.message.shift_start),
					hospitalData,
					targetDayIndex: dayIndex,
					sourceDayIndex: dayIndex,
					chosenDepartment,
					isSnf,
					locationsMap,
				}),
				nurseType: getEmploymentTypeDisplayName(nurse.employmentType),
				...(nurse.nursingStartingDate && {
					nurseStartDate: nurse.nursingStartingDate,
				}),
				selectable: true,
				name: formatPersonFullName(nurse, lastNameFirst),
				bonus: isPartialShift(request, nurseData.id) ? 0 : (request.current_bonus ?? request.bonus ?? 0),
				accepted_start_time: currentRequestData?.accepted_start_time ?? nurseData.accepted_start_time,
				accepted_end_time: currentRequestData?.accepted_end_time ?? nurseData.accepted_end_time,
			};
		});
	}, [
		requestApprovalList,
		getNurseById,
		getWeekIndexByDateTime,
		request,
		hospitalData,
		getDayIndexByDateTime,
		chosenDepartment,
		isSnf,
		locationsMap,
		isPartialShift,
		lastNameFirst,
	]);

	useEffect(() => {
		if (selectedNurseId && queuedDenyIdSet.has(selectedNurseId)) {
			setSelectedNurseId(undefined);
		}
	}, [selectedNurseId, queuedDenyIdSet]);

	const handleRowClick = useCallback(
		(nurseId: number) => {
			setSelectedNurseId(nurseId === selectedNurseId ? undefined : nurseId);
		},
		[selectedNurseId],
	);

	const [showBroadcastMessagePopup, setShowBroadcastMessagePopup] = useState(false);

	const toggleShowBroadcastMessagePopup = useCallback(() => {
		setShowBroadcastMessagePopup((prevState) => !prevState);
	}, []);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				px: 2,
				py: 1,
				gap: 1,
				justifyContent: "space-between",
				minHeight: "300px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
				}}
			>
				<Box
					sx={{
						width: "100%",
						overflow: "clip",
						overflowY: "auto",
					}}
				>
					<MinimizedNurseDataTable
						request={request}
						nurseData={nursesDataFromBackend}
						onRowClick={handleRowClick}
						selectedNurseId={selectedNurseId}
						maxTableHeight={maxTableHeight}
						onDenyNurse={onDenyNurse}
						onRestoreNurse={onRestoreNurse}
						queuedDenyNurseIds={queuedDenyNurseIds}
						loading={isFetching}
						title={
							<Box
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "center",
								}}
							>
								<Typography
									sx={{
										fontWeight: 600,
										fontSize: "14px",
										lineHeight: "14px",
										color: INK.SECONDARY,
									}}
								>
									{request.message.shift_start.toFormat(isMedicalDepartment ? "ccc, MMM d" : "ccc, MMM d | h:mma")}
									{!isMedicalDepartment && request.message.shift_end.toFormat(" - h:mma")}
									{isMedicalDepartment && ` | ${request.location}`}
								</Typography>
								<InitiateRoboCallButton requestId={request.id} iconSizeOverride={"small"} date={request.message.shift_start} />
								{!isMedicalDepartment && (
									<InHouseTooltip title={"Message relevant staff (that have not accepted yet)"}>
										<IconButton size={"medium"} onClick={toggleShowBroadcastMessagePopup}>
											<ChatOutlinedIcon fontSize={"small"} />
										</IconButton>
									</InHouseTooltip>
								)}
							</Box>
						}
					/>
				</Box>
			</Box>
			{selectedNurseId &&
				isPartialShift(request, selectedNurseId) &&
				(() => {
					const selectedNurse = nursesDataFromBackend.find((n) => n.id === selectedNurseId);
					return selectedNurse?.accepted_start_time && selectedNurse?.accepted_end_time ? (
						<Box sx={{ p: 1, backgroundColor: STATUS.ERROR.light, borderRadius: 1, border: `1px solid ${STATUS.ERROR.main}`, mb: 1 }}>
							<Typography variant="body2" sx={{ color: STATUS.ERROR.main, fontWeight: 600, fontSize: "0.85rem" }}>
								⚠️ Partial shift: {selectedNurse.accepted_start_time.toFormat("h:mm a")} - {selectedNurse.accepted_end_time.toFormat("h:mm a")}
							</Typography>
						</Box>
					) : null;
				})()}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 2,
				}}
			>
				{rooms.length > 0 && !isMedicalDepartment && (
					<FormControl size="small" sx={{ width: "180px" }}>
						<SystemDropdown
							label="Location"
							items={rooms}
							selectedItem={selectedRoom}
							onChange={(value) => setSelectedRoom(value ?? "")}
							clearable={!isReplaceRequest}
							disabled={isReplaceRequest}
						/>
					</FormControl>
				)}
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 2,
						ml: "auto",
					}}
				>
					<SystemButton buttonVariant={"borderless"} onClick={onCancel}>
						Cancel
					</SystemButton>
					<SystemButton
						sx={{
							width: "100px",
						}}
						disabled={selectedNurseId === undefined && !hasQueuedDenies}
						loading={isLoading}
						onClick={(event) => {
							const location = selectedRoom || undefined;
							if (selectedNurseId) {
								pendingSubmitRef.current = {
									event,
									suggestionIndex: issue.suggestions.findIndex((suggestion) => suggestion.otherNurseId === selectedNurseId),
									nurseId: selectedNurseId,
									location,
								};
								const weekIndex = getWeekIndexByDateTime(request.message.shift_start);
								const dayIndex = getDayIndexByDateTime(request.message.shift_start);
								const shiftIndex = request.message.shift_type === ShiftType.Day ? 0 : 1;
								const targetWarning = getTargetCheck({ locationName: location, weekIndex, dayNo: dayIndex, shiftIndex, isActual: false });
								checkWarnings(false, undefined, undefined, undefined, false, false, targetWarning);
							} else if (hasQueuedDenies) {
								// Deny-only submit: parent's onSubmit flushes the queue without an approval (no target check — nothing is added).
								onSubmit(event, 0, undefined, location);
							}
						}}
					>
						Submit
					</SystemButton>
				</Box>
			</Box>
			{showBroadcastMessagePopup && (
				<NewBroadcastMessageModal
					initialSmsMessage={getShiftPickupMessage(issue.shiftTimes?.date, issue.shiftTimes?.start, issue.shiftTimes?.end)}
					addAppLink={true}
					defaultWorkingFilter={MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK}
					nurseIdsToIgnore={issue.suggestions.flatMap((s) => (s.otherNurseId ? [s.nurseId, s.otherNurseId] : [s.nurseId]))}
					jobId={request.job_id}
					initialRolesToSelect={request.message.roles
						.map((r) => NurseRole[NurseFullRoleShort[r as keyof typeof NurseFullRoleShort]])
						?.map((r) => NurseFullRole[r as keyof typeof NurseFullRole])}
					onClose={toggleShowBroadcastMessagePopup}
					initialFilters={
						issue.shiftTimes
							? [
									{
										workFilter: MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK,
										dateFilter: issue.shiftTimes.date,
										timeFilter: {
											start: issue.shiftTimes.start,
											end: issue.shiftTimes.end,
										},
									},
								]
							: undefined
					}
				/>
			)}
			<WarningDialog
				openTargetWarning={warningState.isTargetWarningOpen}
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				targetDetails={warningState.targetDetails}
				longShiftDuration={chosenDepartment.longShiftDuration}
				onCancelTarget={handleCancelWarnings}
				onConfirmTarget={handleTargetConfirm}
				onCancelAgency={handleCancelWarnings}
				onConfirmAgency={handleAgencyConfirm}
				onCancelOvertime={handleCancelWarnings}
				onConfirmOvertime={handleOvertimeConfirm}
				onCancelDailyHours={handleCancelWarnings}
				onConfirmDailyHours={handleDailyHoursConfirm}
			/>
		</Box>
	);
};
